import { supabase } from './client';
import { userProfileService } from './userProfileService';
import { createLogger } from '../logger';
import type { User, AuthError, Session } from './types';

const authLog = createLogger('auth');

export const authService = {
  async signUp(email: string, password: string, userData?: { displayName?: string; phone?: string }): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) return { user: null, error };

    if (data.user) {
      try {
        const profileData = userData || {
          displayName: data.user.email?.split('@')[0] || '用戶',
          phone: ''
        };
        await userProfileService.createProfile(data.user.id, profileData);
        authLog.info('註冊時成功創建用戶 profile');
      } catch (profileError) {
        authLog.error('建立用戶資料檔案失敗:', profileError);
      }
    }

    return { user: data.user, error: null };
  },

  async signIn(email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { user: data.user, error };
  },

  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  async getCurrentUser(): Promise<User | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getCurrentSession(): Promise<Session | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  },

  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    return { error };
  },

  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { error };
  },

  async getUserAuthMethods(): Promise<{ methods: string[]; error: AuthError | null }> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        return { methods: [], error };
      }

      const methods: string[] = [];

      if (user.identities) {
        user.identities.forEach(identity => {
          if (identity.provider === 'email') {
            methods.push('password');
          } else {
            methods.push(identity.provider);
          }
        });
      }

      authLog.debug('用戶認證方式:', methods);
      return { methods, error: null };
    } catch (error) {
      authLog.error('檢查認證方式失敗:', error);
      return { methods: [], error: error as AuthError };
    }
  },

  async linkPasswordToOAuthAccount(password: string): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        authLog.error('設定密碼失敗:', error);
        return { success: false, error };
      }

      authLog.info('成功為 OAuth 用戶設定密碼');
      return { success: true, error: null };
    } catch (error) {
      authLog.error('設定密碼異常:', error);
      return { success: false, error: error as AuthError };
    }
  },

  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    authLog.debug('開始 Google OAuth 流程');
    authLog.debug('重定向 URL:', `${window.location.origin}/auth/callback`);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      }
    });

    authLog.debug('OAuth 初始化結果:', { error: error?.message });
    return { error };
  },

  async handleOAuthCallback(): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      authLog.debug('開始處理 OAuth 回調');
      const { data, error } = await supabase.auth.getSession();

      authLog.debug('getSession 結果:', {
        hasSession: !!data?.session,
        hasUser: !!data?.session?.user,
        error: error?.message
      });

      if (error) {
        authLog.error('getSession 錯誤:', error);
        return { user: null, error };
      }

      const user = data.session?.user || null;

      if (user) {
        authLog.debug('找到用戶 session:', {
          userId: user.id,
          email: user.email,
          provider: user.app_metadata?.provider
        });
      }

      if (user && user.app_metadata.provider === 'google') {
        try {
          authLog.debug('檢查 Google OAuth 用戶資料檔案');
          const existingProfile = await userProfileService.getProfile(user.id);

          if (!existingProfile) {
            authLog.debug('建立新的用戶資料檔案');
            const displayName = user.user_metadata?.full_name || user.user_metadata?.name || '';
            await userProfileService.createProfile(user.id, {
              displayName: displayName,
            });
            authLog.info('用戶資料檔案建立成功');
          } else {
            authLog.debug('用戶資料檔案已存在');
          }
        } catch (profileError) {
          authLog.error('建立 OAuth 用戶資料檔案失敗:', profileError);
        }
      }

      return { user, error: null };
    } catch (err) {
      authLog.error('處理 OAuth 回調失敗:', err);
      return { user: null, error: err as AuthError };
    }
  }
};
